import React, { useState } from "react";
import {
  TestimonialCard,
  QuoteMark,
  QuoteText,
  AuthorInfo,
  AuthorName,
  AuthorTitle,
  AvatarRow,
  Avatar,
  PlaceholderAvatar,
} from "./slider.styles";
import { trackEvent } from "../../helpers";

interface IContent {
  id: number;
  text: string;
  author: string;
}

interface SliderProps {
  content: IContent[];
}

// Function to highlight key phrases in testimonials
const highlightText = (text: string): React.ReactNode => {
  // Keywords/phrases to highlight
  const highlightPhrases = [
    "invaluable asset",
    "high-quality",
    "forward-thinking",
    "proactive documentation",
    "consistently delivered",
    "documentation culture",
    "highly recommend",
    "exceptional ability",
    "exceptionally detail-oriented",
    "great leader",
    "perfect balance",
    "compassion and candour",
    "broader vision",
    "user experience",
    "effectively",
    "remarkable results",
  ];

  let result = text;
  highlightPhrases.forEach(phrase => {
    const regex = new RegExp(`(${phrase})`, 'gi');
    result = result.replace(regex, '|||$1|||');
  });

  const parts = result.split('|||');

  return parts.map((part, index) => {
    const isHighlight = highlightPhrases.some(
      phrase => part.toLowerCase() === phrase.toLowerCase()
    );
    return isHighlight ? (
      <span key={index} className="highlight">{part}</span>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    );
  });
};

// Parse author string to extract name and title
const parseAuthor = (author: string): { name: string; title: string } => {
  const parts = author.split(',').map(p => p.trim());
  return {
    name: parts[0] || author,
    title: parts[1] || '',
  };
};

// Get initials from name
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const Slider = ({ content }: SliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSelectTestimonial = (index: number) => {
    setCurrentIndex(index);
    trackEvent("Selected testimonial avatar");
  };

  const { name, title } = parseAuthor(content[currentIndex].author);

  return (
    <TestimonialCard>
      <QuoteMark>"</QuoteMark>

      <QuoteText>
        {highlightText(content[currentIndex].text)}
      </QuoteText>

      <AuthorInfo>
        <AuthorName>{name}</AuthorName>
        {title && <AuthorTitle>{title}</AuthorTitle>}
      </AuthorInfo>

      <AvatarRow>
        {content.map((item, index) => {
          const { name: authorName } = parseAuthor(item.author);
          return (
            <Avatar
              key={item.id}
              isActive={index === currentIndex}
              onClick={() => handleSelectTestimonial(index)}
              aria-label={`View testimonial from ${authorName}`}
            >
              <PlaceholderAvatar>
                {getInitials(authorName)}
              </PlaceholderAvatar>
            </Avatar>
          );
        })}
      </AvatarRow>
    </TestimonialCard>
  );
};
